'use client';

import { useQuery } from '@tanstack/react-query';
import { IconBell } from '@tabler/icons-react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useTranslations } from 'next-intl';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { useNotificationsEnabled } from '@/features/configurations';
import { NOTIFICATIONS_HASH } from '../constants';
import { notificationQueries } from '../services/notifications.queries';
import { NotificationDrawer } from './notification-drawer';

export function NotificationBell({
  fallbackToPolling = true
}: {
  fallbackToPolling?: boolean;
}) {
  const t = useTranslations('notifications');
  const notificationsEnabled = useNotificationsEnabled();
  // #notifications — app-shell drawer open-state in the URL hash.
  const { open, onOpenChange } = useHashDialog(NOTIFICATIONS_HASH.drawer);

  const { data } = useQuery(
    notificationQueries.unreadCountPolling({
      pollingEnabled: fallbackToPolling && notificationsEnabled
    })
  );

  // A disabled feature should not occupy the app shell — a struck-out bell that
  // opens an empty drawer is noise. Every hook above still runs, so flipping the
  // setting remounts nothing and the polling query stays disabled either way.
  if (!notificationsEnabled) return null;

  const unreadCount = data?.data?.count ?? 0;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='ghost'
            size='icon'
            className='relative'
            onClick={() => onOpenChange(true)}
          >
            <IconBell className='h-4 w-4' />
            {unreadCount > 0 && (
              <span className='bg-destructive absolute -end-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white'>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
            <span className='sr-only'>{t('title')}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('title')}</TooltipContent>
      </Tooltip>

      <NotificationDrawer open={open} onOpenChange={onOpenChange} />
    </>
  );
}
